import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { Map as ImmutableMap, OrderedSet } from 'immutable';
import { CommunicationSubscriptionDisplayType, EnumerationDisplayType, StringDisplayType } from 'customer-data-filters-ui/filterQueryFormat/DisplayTypes';
import { makeEventCountField } from '../converters/specialFields/EventCountField';
import { makeEventDateField } from '../converters/specialFields/EventDateField';
import { makeEventStatusField } from '../converters/specialFields/EventStatusField';
import { makeEventSubjectField } from '../converters/specialFields/EventSubjectField';
import I18n from 'I18n';
import { makeEventSubjectVersionField } from '../converters/specialFields/EventSubjectVersionField';
import { makeEventPageField } from '../converters/specialFields/EventPageField';
import { COMMUNICATION_SUBSCRIPTION } from '../converters/objectSeg/ObjectSegFilterTypes';
import Equal from '../filterQueryFormat/operator/Equal';
const EVENT_BASED_FILTER_FAMILIES = Object.freeze([DSAssetFamilies.PAGE_VIEW, DSAssetFamilies.FORM, DSAssetFamilies.EMAIL_CAMPAIGN, DSAssetFamilies.EMAIL_SUBSCRIPTION, DSAssetFamilies.SMS_SUBSCRIPTION, DSAssetFamilies.WHATSAPP_SUBSCRIPTION, DSAssetFamilies.CTA, DSAssetFamilies.EVENT]);
const REFINABLE_EXTRA_FILTER_FAMILIES = Object.freeze([DSAssetFamilies.PAGE_VIEW, DSAssetFamilies.FORM, DSAssetFamilies.CTA, DSAssetFamilies.EVENT, DSAssetFamilies.EMAIL_CAMPAIGN]);
export const EVENT_BASED_FIELD_KEYS = Object.freeze({
  EVENT_SUBJECT: 'hs_event_subject',
  EVENT_SUBJECT_VERSION: 'hs_event_subject_version',
  EVENT_STATUS: 'hs_event_status',
  EVENT_DATE: 'hs_event_date',
  EVENT_COUNT: 'hs_event_count',
  EVENT_PAGE: 'hs_event_page'
});
export const isEventBasedFilterFamily = filterFamily => {
  return EVENT_BASED_FILTER_FAMILIES.includes(filterFamily);
};
const getPageViewFields = () => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(DSAssetFamilies.PAGE_VIEW, StringDisplayType),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_DATE]: {
      field: makeEventDateField(DSAssetFamilies.PAGE_VIEW),
      isSelectable: true
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_COUNT]: {
      field: makeEventCountField(DSAssetFamilies.PAGE_VIEW),
      isSelectable: true
    }
  });
};
const getFormSubmissionFields = data => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(DSAssetFamilies.FORM, EnumerationDisplayType),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT]: {
      field: makeEventSubjectField(DSAssetFamilies.FORM, data),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_PAGE]: {
      field: makeEventPageField(DSAssetFamilies.FORM),
      isSelectable: true
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_DATE]: {
      field: makeEventDateField(DSAssetFamilies.FORM),
      isSelectable: true
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_COUNT]: {
      field: makeEventCountField(DSAssetFamilies.FORM),
      isSelectable: true
    }
  });
};
const getEmailCampaignFields = data => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(DSAssetFamilies.EMAIL_CAMPAIGN, EnumerationDisplayType),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT]: {
      field: makeEventSubjectField(DSAssetFamilies.EMAIL_CAMPAIGN, data, I18n.text(`newCustomerDataFilters.specialFields.EMAIL_CAMPAIGN.${EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT}_placeholder`)),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_DATE]: {
      field: makeEventDateField(DSAssetFamilies.EMAIL_CAMPAIGN),
      isSelectable: true
    }
  });
};
const getEmailSubscriptionFields = () => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(DSAssetFamilies.EMAIL_SUBSCRIPTION, DSAssetFamilies.EMAIL_SUBSCRIPTION),
      isSelectable: false
    }
  });
};
const getCommunicationSubscriptionFields = filterFamily => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(filterFamily, CommunicationSubscriptionDisplayType, COMMUNICATION_SUBSCRIPTION).set('referencedObjectType', filterFamily),
      isSelectable: false
    }
  });
};
const getCTAFields = (data, condition) => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(DSAssetFamilies.CTA, EnumerationDisplayType),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT]: {
      field: makeEventSubjectField(DSAssetFamilies.CTA, data, I18n.text(`newCustomerDataFilters.specialFields.CTA.${EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT}_placeholder`)),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT_VERSION]: {
      field: makeEventSubjectVersionField(DSAssetFamilies.CTA, condition, I18n.text(`newCustomerDataFilters.specialFields.CTA.${EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT_VERSION}_placeholder`)),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_DATE]: {
      field: makeEventDateField(DSAssetFamilies.CTA),
      isSelectable: true
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_COUNT]: {
      field: makeEventCountField(DSAssetFamilies.CTA),
      isSelectable: true
    }
  });
};
const getBehavioralEventFields = data => {
  return ImmutableMap({
    [EVENT_BASED_FIELD_KEYS.EVENT_STATUS]: {
      field: makeEventStatusField(DSAssetFamilies.EVENT, EnumerationDisplayType),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT]: {
      field: makeEventSubjectField(DSAssetFamilies.EVENT, data, I18n.text(`newCustomerDataFilters.specialFields.EVENT.${EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT}_placeholder`)),
      isSelectable: false
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_DATE]: {
      field: makeEventDateField(DSAssetFamilies.EVENT),
      isSelectable: true
    },
    [EVENT_BASED_FIELD_KEYS.EVENT_COUNT]: {
      field: makeEventCountField(DSAssetFamilies.EVENT),
      isSelectable: true
    }
  });
};
export const getEventBasedFilterFamilyFields = (filterFamily, data = ImmutableMap(), condition, onlySelectableFields = false) => {
  let fieldsMap = ImmutableMap();
  if (filterFamily === DSAssetFamilies.PAGE_VIEW) {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    fieldsMap = getPageViewFields(data);
  }
  if (filterFamily === DSAssetFamilies.FORM) {
    fieldsMap = getFormSubmissionFields(data);
  }
  if (filterFamily === DSAssetFamilies.EMAIL_CAMPAIGN) {
    fieldsMap = getEmailCampaignFields(data);
  }
  if (filterFamily === DSAssetFamilies.EMAIL_SUBSCRIPTION) {
    fieldsMap = getEmailSubscriptionFields();
  }
  if (filterFamily === DSAssetFamilies.SMS_SUBSCRIPTION) {
    fieldsMap = getCommunicationSubscriptionFields(filterFamily);
  }
  if (filterFamily === DSAssetFamilies.WHATSAPP_SUBSCRIPTION) {
    fieldsMap = getCommunicationSubscriptionFields(filterFamily);
  }
  if (filterFamily === DSAssetFamilies.CTA) {
    fieldsMap = getCTAFields(data, condition);
  }
  if (filterFamily === DSAssetFamilies.EVENT) {
    fieldsMap = getBehavioralEventFields(data);
  }
  return fieldsMap.filter(item => onlySelectableFields ? item.isSelectable : true).map(item => item.field);
};
export const isRefinableExtra = filterFamily => {
  return REFINABLE_EXTRA_FILTER_FAMILIES.includes(filterFamily);
};
export const getBaseEventBasedFilterFamilyField = filterFamily => {
  const filterFamilyFields = getEventBasedFilterFamilyFields(filterFamily);
  return filterFamilyFields.get(EVENT_BASED_FIELD_KEYS.EVENT_STATUS);
};
const propertyTypeToRefinementFieldNameMap = Object.freeze({
  [PropertyTypes.DATE]: EVENT_BASED_FIELD_KEYS.EVENT_DATE,
  [PropertyTypes.NUMBER]: EVENT_BASED_FIELD_KEYS.EVENT_COUNT
});
export const getRefinementCondition = (filterFamily, refinement) => {
  if (refinement !== null && refinement !== void 0 && refinement.field) {
    const type = refinement.field.type;
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const fieldName = propertyTypeToRefinementFieldNameMap[type];
    const filterFamilyFields = getEventBasedFilterFamilyFields(filterFamily);
    const refinementField = filterFamilyFields.get(fieldName);
    return refinement.set('field', refinementField);
  }
  return undefined;
};
export const getEventBasedFilterOperators = fieldName => {
  const eventBasedFields = [EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT, EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT_VERSION, EVENT_BASED_FIELD_KEYS.EVENT_PAGE];
  return eventBasedFields.includes(fieldName) ? OrderedSet.of(Equal) : undefined;
};
export const shouldAddEventPageRefinement = (__filterFamily, fieldName) => {
  // include page field if field provided is a page field
  if (fieldName === EVENT_BASED_FIELD_KEYS.EVENT_PAGE) {
    return true;
  }
  return false;
};
export const isConditionsPseudoGroup = filterFamily => {
  const conditionsPseudoGroups = [DSAssetFamilies.FORM, DSAssetFamilies.PAGE_VIEW, DSAssetFamilies.EMAIL_CAMPAIGN, DSAssetFamilies.EMAIL_SUBSCRIPTION, DSAssetFamilies.SMS_SUBSCRIPTION, DSAssetFamilies.WHATSAPP_SUBSCRIPTION, DSAssetFamilies.CTA, DSAssetFamilies.EVENT];
  return !!filterFamily && conditionsPseudoGroups.includes(filterFamily);
};